import { useEffect, useMemo, useState } from 'react'
import { ArrowUp, ArrowDown, X, Plus, Search } from 'lucide-react'
import type { NtBlock, NtCourse, StudentDoc } from '../../core/types'
import { useNorseTerm } from './NorseTermData'
import { saveSelection, ranksPerBlock } from './model'
import { ArtifactList } from './ArtifactOptions'
import { fmtDate } from './Overview'
import { Loading, Empty, ErrorState } from '../../ui/States'

/**
 * A student's course selections: their top morning courses and their top
 * afternoon courses, ranked, from the approved catalog. Two lists because
 * every student gets one of each, so neither can be left out by accident.
 * Required for everyone, including students proposing an independent
 * project: a proposal may not be approved, and freshmen take on-site courses.
 *
 * Stored as one list, morning picks then afternoon picks, which is what
 * placement reads (it counts choices within each block). The first time both
 * lists are full the server stamps the time: the student's place in line,
 * kept if they change their picks before the deadline. The rules enforce
 * that, and close writes at the deadline.
 *
 * Laid out to browse: the catalog has filters and full descriptions; the
 * choices sit above it, and on a phone a bar stays at the bottom with the
 * count and a way back up.
 */
export function CourseSelections({ me }: { me: StudentDoc }) {
  const { term, approvedCourses, mySelection } = useNorseTerm()
  const per = ranksPerBlock(term)
  const [lists, setLists] = useState<Record<NtBlock, string[]> | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [changed, setChanged] = useState(false)
  const [filter, setFilter] = useState<'all' | NtBlock>('all')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<Set<string>>(new Set())

  const byId = useMemo(() => new Map((approvedCourses ?? []).map((c) => [c.id, c])), [approvedCourses])

  // Start from what is saved, split back into the two blocks.
  useEffect(() => {
    if (lists !== null || mySelection === null || approvedCourses === null) return
    const saved = mySelection?.ranks ?? []
    setLists({
      AM: saved.filter((id) => byId.get(id)?.block === 'AM'),
      PM: saved.filter((id) => byId.get(id)?.block === 'PM'),
    })
  }, [mySelection, approvedCourses, byId, lists])

  if (approvedCourses === null || lists === null) return <Loading label="Loading the course catalog" />

  const deadline = term.deadlines.rankings
  const closed = !!deadline && Date.now() > new Date(deadline + 'T23:59:59').getTime()
  const ranks = [...lists.AM, ...lists.PM]
  const full = lists.AM.length >= per && lists.PM.length >= per
  const submitted = !!mySelection?.submittedAt
  const dirty = JSON.stringify(ranks) !== JSON.stringify(mySelection?.ranks ?? [])
  const stampedAt = (mySelection?.submittedAt as { toDate?: () => Date } | null)?.toDate?.()
  const name = (b: NtBlock) => (b === 'AM' ? 'Morning' : 'Afternoon')

  const move = (b: NtBlock, i: number, by: -1 | 1) => setLists((l) => {
    const n = [...l![b]]; const j = i + by
    if (j < 0 || j >= n.length) return l
    ;[n[i], n[j]] = [n[j]!, n[i]!]
    return { ...l!, [b]: n }
  })
  const remove = (b: NtBlock, id: string) => setLists((l) => ({ ...l!, [b]: l![b].filter((x) => x !== id) }))
  const add = (c: NtCourse) => setLists((l) => {
    const b = c.block as NtBlock
    if (!b || l![b].includes(c.id) || l![b].length >= per) return l
    return { ...l!, [b]: [...l![b], c.id] }
  })

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      await saveSelection(term.id, { email: me.email, studentId: me.studentId, name: me.displayName,
        seminarTeacherEmail: me.seminarTeacherEmail }, ranks, 2 * per, mySelection ?? undefined)
      setChanged(!!mySelection?.submittedAt)
      setSavedAt(new Date())
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const shown = approvedCourses
    .filter((c) => filter === 'all' || c.block === filter)
    .filter((c) => !q || `${c.title} ${c.teacherName} ${c.desc}`.toLowerCase().includes(q.toLowerCase()))
  const count = (b: NtBlock) => approvedCourses.filter((c) => c.block === b).length

  const list = (b: NtBlock) => (
    <div className="sel__col">
      <div className="sel__colhead">
        <span>{name(b)}</span>
        <span className="work__sub">{lists[b].length} of {per}</span>
      </div>
      <ol className="sel__list">
        {Array.from({ length: per }, (_, i) => {
          const id = lists[b][i]
          const c = id ? byId.get(id) : undefined
          return (
            <li key={i} className={`sel__row${c ? '' : ' is-empty'}`}>
              <span className="sel__n">{i + 1}</span>
              {c ? (
                <>
                  <div className="sel__main">
                    <div style={{ fontWeight: 620 }}>{c.title}</div>
                    <div className="work__sub">{c.teacherName}</div>
                  </div>
                  {!closed && (
                    <div className="sel__acts">
                      <button className="opt__icon" aria-label={`Move ${c.title} up`} disabled={i === 0} onClick={() => move(b, i, -1)}><ArrowUp size={15} aria-hidden="true" /></button>
                      <button className="opt__icon" aria-label={`Move ${c.title} down`} disabled={i === lists[b].length - 1} onClick={() => move(b, i, 1)}><ArrowDown size={15} aria-hidden="true" /></button>
                      <button className="opt__icon opt__icon--danger" aria-label={`Remove ${c.title}`} onClick={() => remove(b, c.id)}><X size={15} aria-hidden="true" /></button>
                    </div>
                  )}
                </>
              ) : <span className="work__sub">{name(b)} choice {i + 1}</span>}
            </li>
          )
        })}
      </ol>
    </div>
  )

  return (
    <>
      <section className="card" style={{ marginTop: 'var(--s-2)' }}>
        <p className="ov__p" style={{ margin: 0 }}>
          You&apos;ll take <strong>one morning course and one afternoon course</strong>. Rank your top {per} of each,
          even if you&apos;re proposing an independent project: if it isn&apos;t approved, these are where you&apos;ll be placed.
          {deadline && <> Due <strong>{fmtDate(deadline, { month: 'long', day: 'numeric' })}</strong>.</>}
        </p>
        <p className="opt__hint" style={{ margin: 'var(--s-2) 0 0' }}>
          First come, first served: your place is set the first time you submit both lists, and you keep it if you change
          your picks before the deadline.
        </p>
      </section>

      {err && <ErrorState body={`Not saved. ${err}`} />}
      {closed && (
        <div className="notice notice--info" role="status">
          <span aria-hidden="true">i</span>
          <div>Course selections closed on {fmtDate(deadline)}. These are the choices placement will use.</div>
        </div>
      )}

      <section id="my-choices" className="card" style={{ marginTop: 'var(--s-3)' }} aria-label="Your choices">
        <h2 className="ov__h">Your choices</h2>
        <div className="sel__cols">
          {list('AM')}
          {list('PM')}
        </div>
        {!closed && (
          <div className="opt__bar">
            <button className="btn" onClick={save} disabled={busy || ranks.length === 0 || (!dirty && (submitted || !full))}>
              {busy ? 'Saving…'
                : !full ? `Save for now (${ranks.length} of ${2 * per})`
                : !submitted ? 'Submit my course selections'
                : dirty ? 'Save my changes' : 'Submitted'}
            </button>
          </div>
        )}
        {!full && !closed && (
          <p className="opt__hint" style={{ textAlign: 'center' }}>
            Still to pick: {[lists.AM.length < per && `${per - lists.AM.length} morning`, lists.PM.length < per && `${per - lists.PM.length} afternoon`].filter(Boolean).join(' and ')}.
          </p>
        )}
        {submitted && stampedAt && (
          <p className="opt__hint" role="status" style={{ textAlign: 'center' }}>
            Submitted {stampedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at{' '}
            {stampedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. That&apos;s your place in line,
            and it stays if you change your picks.{savedAt && changed && !dirty ? ' Changes saved.' : ''}
          </p>
        )}
      </section>

      <h2 className="work__heading" style={{ marginTop: 'var(--s-5)' }}>The course catalog</h2>
      {approvedCourses.length === 0 ? (
        <Empty title="No approved courses yet" body="Courses appear here once teachers' proposals are approved. Check back soon." />
      ) : (
        <>
          <div className="filters">
            <div className="chips" role="tablist" aria-label="Show courses">
              {([['all', `All (${approvedCourses.length})`], ['AM', `Morning (${count('AM')})`], ['PM', `Afternoon (${count('PM')})`]] as const).map(([k, label]) => (
                <button key={k} role="tab" aria-selected={filter === k} className={`chip${filter === k ? ' is-on' : ''}`} onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
            <label className="sel__search">
              <Search size={15} aria-hidden="true" />
              <input className="opt__input" placeholder="Search courses" aria-label="Search courses" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
          </div>
          {shown.length === 0 ? <p className="muted-note">No courses match.</p> : (
            <ul className="sel__cards">
              {shown.map((c) => {
                const b = c.block as NtBlock | null
                const at = b ? lists[b].indexOf(c.id) : -1
                const isOpen = open.has(c.id)
                return (
                  <li key={c.id} className="card sel__card">
                    <div className="sel__cardhead">
                      <div>
                        <div style={{ fontWeight: 650, fontSize: 'var(--t-md)' }}>{c.title}</div>
                        <div className="work__sub">{c.teacherName}{c.room ? ` · ${c.room}` : ''}</div>
                      </div>
                      <span className="pill">{b ? name(b) : 'Block not set'}</span>
                    </div>
                    <p className={`ov__p sel__desc${isOpen ? ' is-open' : ''}`}>{c.desc}</p>
                    {isOpen && (c.artifactOptions ?? []).length > 0 && (
                      <>
                        <div className="opt__label">What you can make for the open house</div>
                        <ArtifactList options={c.artifactOptions} />
                      </>
                    )}
                    <div className="sel__cardfoot">
                      <button className="linkish" onClick={() => setOpen((s) => { const n = new Set(s); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n })}>
                        {isOpen ? 'Show less' : 'Read more'}
                      </button>
                      {!closed && b && (
                        at >= 0
                          ? <span className="pill pill--good">{name(b)} choice {at + 1}</span>
                          : <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={lists[b].length >= per} onClick={() => add(c)}>
                              <Plus size={14} aria-hidden="true" />{' '}
                              {lists[b].length >= per ? `${name(b)} list is full` : `Add as ${name(b).toLowerCase()} choice ${lists[b].length + 1}`}
                            </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}

      {/* On a phone the choices scroll away while browsing; this keeps the count in reach. */}
      {!closed && (
        <div className="sel__tray">
          <span>Morning {lists.AM.length}/{per} · Afternoon {lists.PM.length}/{per}</span>
          <a href="#my-choices" className="btn" style={{ width: 'auto' }}>{full && !submitted ? 'Review and submit' : 'Your choices'}</a>
        </div>
      )}
    </>
  )
}
